import { useMemo } from 'react';

import { User as Auth0User, useAuth0 } from '@auth0/auth0-react';

import { ActorRole } from '../lib/graphql/generated/graphql';

// Internal engineers. Used as a feature whitelist (Versions, ReopenNegotiation)
// and surfaced to analytics as the `isDeveloper` user property.
const DEVELOPER_EMAILS = [
  'alexandre.c@appchoose.io',
  'arnaud@appchoose.io',
  'enzo.viala@appchoose.io',
  'manu@appchoose.io',
  'severin@appchoose.io',
  'thibaut@appchoose.io',
  'daniel.osokin@appchoose.io',
  'florian.arderighi@appchoose.io',
];

// E-merchandising team. Allowed to reopen a closed negotiation within the
// start-date lock window (J-7), same as developers.
const EMERCH_EMAILS = [
  'lola.jacquot@appchoose.io',
  'lou.guilbert@appchoose.io',
  'lina.harutyunyan@appchoose.io',
  'juliette.renard@appchoose.io',
  'lucie.noblanc@appchoose.io',
  'gaelle@appchoose.io',
  'morgane.micheau@appchoose.io',
  'mina@appchoose.io',
  'angie.thomas@appchoose.io',
];

export interface User {
  providerUser: Auth0User;
  isInternalUser: boolean;
  isDeveloper: boolean;
  isEmerch: boolean;
  role: ActorRole;
  canAccessVersions: boolean;
  canReopenNegotiation: boolean;
  canBypassReopenStartDateLock: boolean;
}

export const composeUser = (user: Auth0User): User => {
  const isInternalUser = user?.['https://appchoose.io/claims/isInternalUser'];
  // Auth0 emails can carry uppercase characters depending on the identity
  // provider, so normalize before matching the (lowercase) whitelists.
  const email = user.email?.toLowerCase();
  const isDeveloper = email ? DEVELOPER_EMAILS.includes(email) : false;
  const isEmerch = email ? EMERCH_EMAILS.includes(email) : false;
  return {
    providerUser: user,
    isInternalUser,
    isDeveloper,
    isEmerch,
    role: isInternalUser ? ActorRole.Choose : ActorRole.Brand,
    canAccessVersions: isDeveloper,
    canReopenNegotiation: isInternalUser,
    canBypassReopenStartDateLock: isDeveloper || isEmerch,
  };
};
export function useUser(): User | undefined {
  const { user } = useAuth0();
  return useMemo(() => {
    if (!user) return undefined;
    return composeUser(user);
  }, [user]);
}
